/**
 * CharaKarakaCalculator - Lazy calculator for D7 Chara Karaka table data
 * Provides async calculation of 7 significators with progress tracking
 */

import { JatakaResponse } from '../../../../../models/api';
import { lazyCalculator } from '../lazy/SimpleLazyCalculator';

/**
 * Chara Karaka result interface
 */
export interface CharaKarakaResult {
    significators: Array<{
        rank: number;
        name: string;
        description: string;
        planet: string;
        planetLongitude: number;
    }>;
    calculationTime: number;
    totalPlanets: number;
}

/**
 * Calculate Chara Karaka (7 significators) - simplified for performance
 */
async function calculateCharaKaraka(
    apiResponse: JatakaResponse
): Promise<CharaKarakaResult> {
    const startTime = Date.now();

    // For now, return mock data - actual calculation would be complex
    // This demonstrates the plugin system structure
    await new Promise(resolve => setTimeout(resolve, 50)); // Simulate calculation time

    const mockResult: CharaKarakaResult = {
        significators: [
            { rank: 1, name: "Atma Karaka", description: "Soul Significator", planet: "Su", planetLongitude: 180.5 },
            { rank: 2, name: "Amatya Karaka", description: "Minister Significator", planet: "Ma", planetLongitude: 165.3 },
            { rank: 3, name: "Bhratri Karaka", description: "Brother Significator", planet: "Me", planetLongitude: 155.7 },
            { rank: 4, name: "Matri Karaka", description: "Mother Significator", planet: "Ve", planetLongitude: 145.2 },
            { rank: 5, name: "Putri Karaka", description: "Child Significator", planet: "Ju", planetLongitude: 135.8 },
            { rank: 6, name: "Gnati Karaka", description: "Enemy Significator", planet: "Sa", planetLongitude: 125.4 },
            { rank: 7, name: "Dara Karaka", description: "Spouse Significator", planet: "Mo", planetLongitude: 115.9 }
        ],
        calculationTime: Date.now() - startTime,
        totalPlanets: apiResponse.graha_positions?.planets?.length || 0
    };

    // Minimal logging moved to SimpleLazyCalculator

    return mockResult;
}

/**
 * Generate cache key for Chara Karaka calculation
 */
function generateCacheKey(apiResponse: JatakaResponse): string {
    if (!apiResponse.graha_positions?.planets) {
        return 'no-planets';
    }

    // Create cache key from planet longitudes (simplified)
    const planetPositions = apiResponse.graha_positions.planets
        .map(p => `${p.short_name}:${p.position}`)
        .join('|');

    return planetPositions;
}

// Register with SimpleLazyCalculator - only 4 lines needed!
lazyCalculator.register({
    id: 'chara-karaka-d7',
    calculator: calculateCharaKaraka,
    cacheKey: generateCacheKey,
    ttl: 1800000 // 30 minutes - manual invalidation when positions change
});

export { calculateCharaKaraka, generateCacheKey };